import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { EmpresaSidebar } from "@/components/empresa/EmpresaSidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useCompanyAccess } from "@/hooks/useCompanyAccess";
import { Building2, MapPin, Globe, Heart, Link as LinkIcon, Loader2, Save, Crown, Trash2, Coins } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { CardDescription } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { StateCitySelector } from "@/components/StateCitySelector";

const SECTORS = [
  "Tecnologia",
  "Varejo",
  "Indústria",
  "Saúde",
  "Educação",
  "Serviços",
  "Construção",
  "Agronegócio",
  "Logística",
  "Financeiro",
  "Consultoria",
  "Marketing",
  "Alimentação",
  "Outro"
];

const EMPLOYEE_COUNTS = [
  "1-10",
  "11-50",
  "51-200",
  "201-500",
  "501-1000",
  "1001+"
];

// Máscara de CNPJ: ##.###.###/####-##
const formatCNPJ = (value: string): string => {
  const digits = value.replace(/\D/g, "").slice(0, 14);
  return digits
    .replace(/(\d{2})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1.$2")
    .replace(/(\d{3})(\d)/, "$1/$2")
    .replace(/(\d{4})(\d)/, "$1-$2");
};

// Máscara de telefone: (##) #####-#### ou (##) ####-####
const formatPhone = (value: string): string => {
  const digits = value.replace(/\D/g, "").slice(0, 11);
  if (digits.length <= 10) {
    return digits
      .replace(/(\d{2})(\d)/, "($1) $2")
      .replace(/(\d{4})(\d)/, "$1-$2");
  }
  return digits
    .replace(/(\d{2})(\d)/, "($1) $2")
    .replace(/(\d{5})(\d)/, "$1-$2");
};

// Dynamic plan info based on actual plan values
const getPlanInfo = (plan: string | null): { label: string; variant: "default" | "secondary" | "accent"; benefits: string[] } => {
  // Start/Free plans
  if (!plan || plan === "gratuito" || plan === "start") {
    return {
      label: "Start",
      variant: "secondary",
      benefits: [
        "1 vaga ativa por 30 dias",
        "Divulgação básica",
        "Painel de gerenciamento"
      ]
    };
  }
  
  // Professional plans with dynamic job count
  if (plan.startsWith("profissional_")) {
    const vagas = plan.replace("profissional_", "");
    return {
      label: `Profissional (${vagas} vagas/mês)`,
      variant: "accent",
      benefits: [
        `Até ${vagas} vagas ativas por mês`,
        "Todas as funcionalidades",
        "Filtros avançados",
        "Suporte"
      ]
    };
  }
  
  // Legacy fallback for old plan names
  return {
    label: plan.charAt(0).toUpperCase() + plan.slice(1),
    variant: "default",
    benefits: ["Plano personalizado"]
  };
};

export default function EmpresaMinhaContaPage() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { loading, companyId, companyProfile, isOwner } = useCompanyAccess();
  const [saving, setSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isFirstAccess, setIsFirstAccess] = useState(false);

  const handleDeleteAccount = async () => {
    if (!companyId || !isOwner) return;
    
    setIsDeleting(true);
    
    try {
      // Delete in order: application_answers -> applications -> job_questions -> jobs -> recruiters -> company_profiles -> user_roles
      const { data: jobs } = await supabase
        .from("jobs")
        .select("id")
        .eq("company_id", companyId);

      if (jobs && jobs.length > 0) {
        const jobIds = jobs.map(j => j.id);
        
        // Delete application_answers for applications of these jobs
        const { data: applications } = await supabase
          .from("applications")
          .select("id")
          .in("job_id", jobIds);
        
        if (applications && applications.length > 0) {
          const appIds = applications.map(a => a.id);
          await supabase.from("application_answers").delete().in("application_id", appIds);
          await supabase.from("applications").delete().in("job_id", jobIds);
        }
        
        // Delete job_questions
        await supabase.from("job_questions").delete().in("job_id", jobIds);
        
        // Delete jobs
        await supabase.from("jobs").delete().eq("company_id", companyId);
      }
      
      // Delete company profile
      await supabase.from("company_profiles").delete().eq("id", companyId);
      
      // Delete user role
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await supabase.from("user_roles").delete().eq("user_id", user.id);
      }
      
      // Sign out
      await supabase.auth.signOut();
      
      toast({
        title: "Conta excluída",
        description: "Sua conta foi excluída com sucesso.",
      });
      
      navigate("/");
    } catch (error) {
      console.error("Error deleting account:", error);
      toast({
        title: "Erro",
        description: "Erro ao excluir conta. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const [formData, setFormData] = useState({
    company_name: "",
    cnpj: "",
    phone: "",
    corporate_name: "",
    sector: "",
    employee_count: "",
    description: "",
    city: "",
    state: "",
    website: "",
    culture_values: "",
    linkedin_url: "",
    instagram_url: "",
  });

  useEffect(() => {
    if (companyProfile) {
      setIsFirstAccess(!companyProfile.profile_completed);
      setFormData({
        company_name: companyProfile.company_name || "",
        cnpj: companyProfile.cnpj ? formatCNPJ(companyProfile.cnpj) : "",
        phone: companyProfile.phone ? formatPhone(companyProfile.phone) : "",
        corporate_name: "",
        sector: companyProfile.sector || "",
        employee_count: companyProfile.employee_count || "",
        description: companyProfile.description || "",
        city: companyProfile.city || "",
        state: companyProfile.state || "",
        website: companyProfile.website || "",
        culture_values: companyProfile.culture_values || "",
        linkedin_url: companyProfile.linkedin_url || "",
        instagram_url: companyProfile.instagram_url || "",
      });
    }
  }, [companyProfile]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!companyId) return;

    setSaving(true);

    const updateData: Record<string, unknown> = {
      company_name: formData.company_name,
      cnpj: formData.cnpj.replace(/\D/g, "") || null,
      phone: formData.phone.replace(/\D/g, "") || null,
      sector: formData.sector || null,
      employee_count: formData.employee_count || null,
      description: formData.description || null,
      city: formData.city || null,
      state: formData.state || null,
      website: formData.website || null,
      culture_values: formData.culture_values || null,
      linkedin_url: formData.linkedin_url || null,
      instagram_url: formData.instagram_url || null,
    };

    // Mark profile as completed on first save
    if (isFirstAccess) {
      updateData.profile_completed = true;
    }

    const { error } = await supabase
      .from("company_profiles")
      .update(updateData)
      .eq("id", companyId);

    setSaving(false);

    if (error) {
      toast({
        title: "Erro",
        description: "Erro ao salvar informações.",
        variant: "destructive",
      });
      return;
    }

    if (isFirstAccess) {
      setIsFirstAccess(false);
    }

    toast({
      title: "Sucesso",
      description: "Informações da empresa atualizadas com sucesso!",
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      
      <div className="flex-1 flex">
        <EmpresaSidebar />
        
        <main className="flex-1 p-4 md:p-6 lg:p-8">
          <div className="mobile-container max-w-3xl md:mx-auto pt-14 md:pt-0">
            <h1 className="text-3xl font-bold text-foreground mb-2">Minha Conta</h1>
            <p className="text-muted-foreground mb-8">
              Complete as informações da sua empresa para atrair os melhores candidatos.
            </p>

            {/* Banner de boas-vindas para primeiro acesso */}
            {isFirstAccess && (
              <div className="bg-primary/10 border border-primary/30 text-primary-foreground p-4 rounded-lg mb-6">
                <h3 className="font-semibold text-primary mb-1">Bem-vindo ao JobWayX!</h3>
                <p className="text-sm text-muted-foreground">
                  Complete as informações da sua empresa para começar a publicar vagas e atrair os melhores talentos.
                </p>
              </div>
            )}

          {/* Seção de Créditos */}
          {companyProfile && (companyProfile as { credits?: number }).credits !== undefined && (companyProfile as { credits?: number }).credits! > 0 && (
            <Card className="mb-6 border-success/20 bg-success/5">
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <Coins className="h-5 w-5 text-success" />
                  <span className="font-medium">Créditos Disponíveis:</span>
                  <Badge variant="default" className="bg-success text-success-foreground">
                    {(companyProfile as { credits?: number }).credits}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground mt-2">
                  Cada crédito permite publicar 1 vaga por 30 dias.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Seção do Plano */}
          {companyProfile && (() => {
            const planInfo = getPlanInfo(companyProfile.plan);
            return (
              <Card className="mb-6 border-primary/20 bg-primary/5">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Crown className="h-5 w-5 text-primary" />
                    Plano da Empresa
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="space-y-3">
                      <div className="flex items-center gap-2">
                        <span className="text-muted-foreground">Plano Atual:</span>
                        <Badge variant={planInfo.variant}>
                          {planInfo.label}
                        </Badge>
                      </div>
                      <ul className="space-y-1 text-sm text-muted-foreground">
                        {planInfo.benefits.map((benefit, index) => (
                          <li key={index} className="flex items-center gap-2">
                            <span className="text-primary">•</span>
                            {benefit}
                          </li>
                        ))}
                      </ul>
                    </div>
                    {isOwner && (
                      <Link to="/planos">
                        <Button variant="outline" className="gap-2">
                          <Crown className="h-4 w-4" />
                          Ver Planos
                        </Button>
                      </Link>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })()}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Informações Básicas */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-primary" />
                  Informações Básicas
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="company_name">Nome da Empresa *</Label>
                    <Input
                      id="company_name"
                      value={formData.company_name}
                      onChange={(e) => handleInputChange("company_name", e.target.value)}
                      required
                      disabled={!isOwner}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="cnpj">CNPJ</Label>
                    <Input
                      id="cnpj"
                      value={formData.cnpj}
                      onChange={(e) => handleInputChange("cnpj", formatCNPJ(e.target.value))}
                      placeholder="00.000.000/0000-00"
                      disabled={!isOwner}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Telefone</Label>
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => handleInputChange("phone", formatPhone(e.target.value))}
                      placeholder="(00) 00000-0000"
                      disabled={!isOwner}
                    />
                  </div>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="sector">Setor / Segmento</Label>
                    <Select
                      value={formData.sector}
                      onValueChange={(value) => handleInputChange("sector", value)}
                      disabled={!isOwner}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o setor" />
                      </SelectTrigger>
                      <SelectContent>
                        {SECTORS.map((sector) => (
                          <SelectItem key={sector} value={sector}>
                            {sector}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="employee_count">Número de Funcionários</Label>
                    <Select
                      value={formData.employee_count}
                      onValueChange={(value) => handleInputChange("employee_count", value)}
                      disabled={!isOwner}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione a faixa" />
                      </SelectTrigger>
                      <SelectContent>
                        {EMPLOYEE_COUNTS.map((count) => (
                          <SelectItem key={count} value={count}>
                            {count} funcionários
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Descrição da Empresa (história)</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => handleInputChange("description", e.target.value)}
                    placeholder="Conte um pouco sobre a história e o que sua empresa faz..."
                    rows={4}
                    disabled={!isOwner}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Localização */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-primary" />
                  Localização
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <StateCitySelector
                  state={formData.state}
                  city={formData.city}
                  onStateChange={(value) => handleInputChange("state", value)}
                  onCityChange={(value) => handleInputChange("city", value)}
                  disabled={!isOwner}
                />

                <div className="space-y-2">
                  <Label htmlFor="website" className="flex items-center gap-2">
                    <Globe className="h-4 w-4" />
                    Site Oficial
                  </Label>
                  <Input
                    id="website"
                    type="url"
                    value={formData.website}
                    onChange={(e) => handleInputChange("website", e.target.value)}
                    placeholder="https://www.suaempresa.com.br"
                    disabled={!isOwner}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Identidade da Marca */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Heart className="h-5 w-5 text-primary" />
                  Identidade da Marca (opcional)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <Label htmlFor="culture_values">Valores e Cultura</Label>
                  <Textarea
                    id="culture_values"
                    value={formData.culture_values}
                    onChange={(e) => handleInputChange("culture_values", e.target.value)}
                    placeholder="Descreva os valores, missão e cultura da sua empresa..."
                    rows={4}
                    disabled={!isOwner}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Links Oficiais */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <LinkIcon className="h-5 w-5 text-primary" />
                  Links Oficiais
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="linkedin_url">LinkedIn da Empresa</Label>
                  <Input
                    id="linkedin_url"
                    type="url"
                    value={formData.linkedin_url}
                    onChange={(e) => handleInputChange("linkedin_url", e.target.value)}
                    placeholder="https://www.linkedin.com/company/suaempresa"
                    disabled={!isOwner}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="instagram_url">Instagram</Label>
                  <Input
                    id="instagram_url"
                    type="url"
                    value={formData.instagram_url}
                    onChange={(e) => handleInputChange("instagram_url", e.target.value)}
                    placeholder="https://www.instagram.com/suaempresa"
                    disabled={!isOwner}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Botões - apenas para o dono */}
            {isOwner && (
              <div className="flex flex-col-reverse gap-3 md:flex-row md:justify-between md:items-center">
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="destructive" className="gap-2" disabled={isDeleting}>
                      {isDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                      Excluir Conta
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Tem certeza que deseja excluir a conta da empresa?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Esta ação é irreversível. Todas as vagas, candidaturas recebidas e informações da empresa serão permanentemente deletados.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction 
                        onClick={handleDeleteAccount} 
                        className="bg-destructive hover:bg-destructive/90"
                      >
                        Sim, excluir conta da empresa
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
                <Button type="submit" disabled={saving} className="gap-2">
                  {saving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4" />
                  )}
                  Salvar Informações
                </Button>
              </div>
            )}
          </form>
          </div>
        </main>
      </div>

      <Footer />
    </div>
  );
}
